import { useEffect, useState, type ReactNode } from 'react';

import {
  Box,
  Divider,
  Link,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';

import Drawer from '@/components/wrapped/Drawer';
import {
  type PaywallProvider,
  type ProviderAnswers,
} from '@/cam-admin/paywall/Shortlist.fixtures';
import { isVisibleToFamily } from '@/cam-admin/paywall/useShortlists';
import {
  ACTIONS,
  DRAWER,
  PUBLISHED,
  statusLabel,
} from '@/cam-admin/paywall/Shortlist.content';

type CamEntry = Omit<ProviderAnswers, 'source' | 'enteredBy' | 'enteredAt'>;

// What a row cannot hold, on a row click.
//
// The note is the reason this exists. A table can show a status in a chip but it
// cannot show a paragraph, and the useful thing about a provider is usually the
// sentence explaining why they are in the state they are in — the branch manager
// is keen but wants the funding picture first, that sort of thing. Truncating
// that into a cell loses exactly the part worth keeping.
//
// Reuses the wrapped Drawer, which is an in-tree overlay anchored to its nearest
// positioned ancestor rather than a portal, so it stays inside the scaled laptop
// frame instead of escaping it.

// A labelled fact, present or not. An absent one says so rather than vanishing:
// this is a CAM tool, and the useful thing about a half-answered row is which half
// is missing. Muted, so 'Not given' reads as an absence rather than as content.
//
// `action` is the control that changes the fact, on the same line as the fact
// itself. One field has one — visibility — and it earns it: a CAM reading Hidden is
// exactly the person deciding whether it should stay that way, and sending them to
// the table for the button would be the drawer answering a question and then
// hiding the answer to it.
const Field = ({
  label,
  value,
  action,
}: {
  label: string;
  value?: string;
  action?: ReactNode;
}) => (
  <Stack spacing={0.25}>
    <Stack sx={{ alignItems: 'center' }} direction='row' spacing={2}>
      <Typography sx={{ flex: 1 }} variant='overline' color='text.secondary'>
        {label}
      </Typography>
      {action}
    </Stack>
    <Typography
      variant='body2'
      color={value ? 'text.primary' : 'text.secondary'}
    >
      {value || DRAWER.notGiven}
    </Typography>
  </Stack>
);

// What edit mode is holding. Strings, exactly what the CAM typed, until the
// moment it is committed.
type Draft = {
  contact: string;
  directNumber: string;
  note: string;
};

const draftFrom = (provider: PaywallProvider): Draft => ({
  contact: provider.answers?.contact ?? '',
  directNumber: provider.answers?.directNumber ?? '',
  note: provider.note ?? '',
});

const toAnswers = (draft: Draft): CamEntry => ({
  contact: draft.contact,
  directNumber: draft.directNumber,
});

// Typing up a phone call, in the drawer, because the drawer is where a CAM is
// already looking when they remember they have something to add.
//
// The same two fields the provider would have tapped themselves, and the note in
// the same pass. Nothing about the request: earliest start and minimum package
// came off both routes in, because every one of them was asking somebody to
// retype what the care assessment already holds. Controlled from the drawer
// rather than holding its own state: the buttons that commit it are in the
// footer, which is the drawer's slot rather than this component's, so the draft
// has to live where both can reach it.
const CamAnswerForm = ({
  draft,
  onChange,
  canEditAnswers,
}: {
  draft: Draft;
  onChange: (patch: Partial<Draft>) => void;
  /**
   * False on provider-entered answers. What they gave stays read-only above, and
   * the note is the only thing this form offers, because a CAM overwriting what
   * a provider tapped is the one edit that costs us something.
   */
  canEditAnswers: boolean;
}) => {
  const field = (label: string, key: keyof Draft, helperText?: string) => (
    <TextField
      label={label}
      value={draft[key]}
      onChange={(e) => onChange({ [key]: e.target.value })}
      helperText={helperText}
      size='small'
      fullWidth
    />
  );

  return (
    <Stack spacing={3}>
      {canEditAnswers && (
        <Stack spacing={2}>
          {field(DRAWER.contact, 'contact')}
          {field(DRAWER.phone, 'directNumber')}
        </Stack>
      )}

      {/* The note, in the same edit pass. It used to save as you type, which was
          the right call when it was the only editable thing in here: now that the
          drawer has a read mode, a field that commits on its own while everything
          around it waits for Save is two rules on one screen. */}
      <Stack spacing={1}>
        <Typography variant='overline' color='text.secondary'>
          {DRAWER.note}
        </Typography>
        <TextField
          multiline
          minRows={4}
          fullWidth
          size='small'
          placeholder={DRAWER.notePlaceholder}
          value={draft.note}
          onChange={(e) => onChange({ note: e.target.value })}
        />
      </Stack>
    </Stack>
  );
};

CamAnswerForm.displayName = 'CamAnswerForm';

const RowDrawer = ({
  provider,
  isPublished,
  isPaywall = true,
  onNote,
  onCamAnswers,
  onMakeVisible,
  onClose,
}: {
  /** null closes the drawer. Kept mounted so it can slide out. */
  provider: PaywallProvider | null;
  isPublished: boolean;
  /**
   * Whether the list this row belongs to runs the paywall. False on a care home
   * list, where the status is the CAM's own and there is no payment gate, so the
   * Visibility field and its override have nothing to say.
   */
  isPaywall?: boolean;
  onNote: (id: string, note: string) => void;
  onCamAnswers: (id: string, answers: CamEntry) => void;
  onMakeVisible: (id: string) => void;
  onClose: () => void;
}) => {
  // The draft, and null for read mode. A drawer that opened straight into a form
  // would put a row a CAM only wanted to read into an editing state, and five
  // empty fields read as work outstanding rather than as an offer.
  //
  // Held here rather than beside the form, because Save and Cancel are in the
  // footer: the drawer owns that slot, so it has to own the draft they commit.
  // Cleared whenever the drawer moves to another provider or closes, so it never
  // reopens mid-edit on somebody else's row.
  const [draft, setDraft] = useState<Draft | null>(null);
  const editing = draft !== null;
  const answers = provider?.answers;
  const isCamEntered = answers?.source === 'cam';
  // Nothing yet, or our own transcription. Provider-entered answers are the one
  // kind edit mode leaves alone.
  const canEditAnswers = !answers || isCamEntered;

  useEffect(() => setDraft(null), [provider?.id]);

  const close = () => {
    setDraft(null);
    onClose();
  };

  const save = () => {
    if (!provider || !draft) return;
    if (canEditAnswers) onCamAnswers(provider.id, toAnswers(draft));
    onNote(provider.id, draft.note);
    setDraft(null);
  };

  return (
    <Drawer
      open={Boolean(provider)}
      title={provider?.name ?? ''}
      // Where they are, and nothing else. No tier, because it decides nothing on
      // this screen now, and no status either: there is one status and it is a
      // field below, so putting it up here would be the second status again under
      // a different name.
      subtitle={provider?.address}
      onClose={close}
      footer={
        // One pair of buttons, in one place, whichever mode the drawer is in:
        // Edit and Done while reading, Cancel and Save while editing. Save sits
        // where Done sat, because it is the same gesture — finish with this row —
        // and a CAM who has just typed something aims at the same corner either
        // way.
        //
        // Edit is offered on every row, since the note is the CAM's own words and
        // always theirs to change. What edit mode lets them touch is what differs:
        // provider-entered answers stay read-only inside it.
        //
        // Each button full width inside a flexed cell, so the pair splits the
        // footer in half. The Box is what does the splitting: llama's Button at
        // isFullWidth is width 100%, which needs a parent to be 100% of.
        <Stack direction='row' spacing={2}>
          <Box sx={{ flex: 1 }}>
            {editing ? (
              <Button
                ariaLabel={DRAWER.camEntry.cancel}
                variant='secondary'
                isFullWidth
                onPress={() => setDraft(null)}
              >
                {DRAWER.camEntry.cancel}
              </Button>
            ) : (
              <Button
                ariaLabel={DRAWER.camEntry.button}
                variant='secondary'
                isFullWidth
                onPress={() => provider && setDraft(draftFrom(provider))}
              >
                {DRAWER.camEntry.button}
              </Button>
            )}
          </Box>
          <Box sx={{ flex: 1 }}>
            {editing ? (
              <Button
                ariaLabel={DRAWER.camEntry.save}
                isFullWidth
                onPress={save}
              >
                {DRAWER.camEntry.save}
              </Button>
            ) : (
              <Button ariaLabel={DRAWER.done} isFullWidth onPress={close}>
                {DRAWER.done}
              </Button>
            )}
          </Box>
        </Stack>
      }
    >
      {provider && (
        <Stack spacing={3}>
          {/* Out to the provider's own page, first thing. A CAM deciding whether
              to put this home in front of a family is asking a question the
              shortlist cannot answer — what the place is actually like — and the
              full record already exists in Care search. New tab rather than a
              navigation: the shortlist is the job, the profile is the check, and
              coming back to a list you had scrolled and a drawer you had open is
              not something to make somebody redo. */}
          <Link
            href={`/care-search?provider=${encodeURIComponent(provider.name)}`}
            target='_blank'
            rel='noopener'
            variant='body2'
          >
            {DRAWER.fullPage}
          </Link>

          <Divider />

          {/* Who answers, and the number that reaches them. This is the whole of
            what we hold per provider now: nothing about the request, because the
            care assessment holds that and asking a provider to retype it turned
            the page into a form at the moment it should be a decision.
            One number, and the best one we have: the direct line they gave us on
            the paywall replaces the switchboard number off the seed data. Two
            fields would leave a CAM choosing between them, and the whole point
            of asking was to get the line that gets answered.
            Hidden only when the form has taken its place. Provider-entered
            answers are read-only in both modes, so on those rows this stays put
            while the note below is edited — a CAM writing up a call needs to see
            what the provider already said. */}
          {!(editing && canEditAnswers) && (
            <>
              <Stack spacing={2}>
                <Field label={DRAWER.contact} value={answers?.contact} />
                <Field
                  label={DRAWER.phone}
                  value={answers?.directNumber ?? provider.phone}
                />
                {/* The inbox the link went to, which is not the same thing as the
                    number: it is usually a central address, and a CAM chasing a
                    provider who has not opened it needs to know which one we
                    have. Read-only — it came off the provider record, not from
                    anybody's answer. */}
                <Field label={DRAWER.email} value={provider.email} />
              </Stack>

              <Divider />
            </>
          )}

          {/* The one status, then every step behind it with its timestamp. The row
            has room for the word and the latest time; this is where the sequence
            that produced it can be read. */}
          <Stack spacing={2}>
            <Field
              label={DRAWER.status}
              // The CAM's own word on a care home row. Same field, because a CAM
              // opening a row is asking the same question either way; the answer
              // comes from whichever status model the list runs.
              value={
                provider.contactState ??
                statusLabel(provider.state, isPublished)
              }
            />
            {/* Visibility next, in the order the table has it: both are state, and
              the pair answers the two questions a CAM opens a row with — where
              this provider got to, and whether the family can see them. Derived
              the same way as the column so the drawer cannot say Visible on a row
              the table calls Hidden.
              The override sits on the label line, and only where it does anything:
              a published list, a row the family cannot see. The tooltip is the
              rule rather than a description of the link, because the thing a CAM
              does not know is that they are allowed to do this at all.
              The drawer is also the only place the override lives now: it was a
              primary button repeated down the table, which made backing a
              provider the loudest thing on a screen for reading status and
              offered the decision without the provider in front of you.
              Absent on a care home row. Nothing gates a home, so the field would
              say Visible with no override to offer and no payment to wait for. */}
            {isPaywall && (
              <Field
                label={DRAWER.visibility}
                // Three answers, not two. A provider who qualifies on a list that
                // has not gone out yet is neither Visible nor Hidden in any useful
                // sense: the family sees nothing at all, and this one is already
                // settled for the moment it does.
                value={
                  !isVisibleToFamily(provider)
                    ? PUBLISHED.hidden
                    : isPublished
                      ? PUBLISHED.visible
                      : PUBLISHED.whenPublished
                }
                // Offered on a draft too. Deciding to back a provider is not a
                // thing a CAM should have to wait for the send to do: settling it
                // while assembling the list is the point at which they are actually
                // thinking about who the family should see. Hidden only once the
                // answer is already yes, where the link would do nothing.
                action={
                  !isVisibleToFamily(provider) && (
                    <Tooltip title={ACTIONS.makeVisibleHint}>
                      <Link
                        component='button'
                        type='button'
                        variant='body2'
                        onClick={() => onMakeVisible(provider.id)}
                      >
                        {ACTIONS.makeVisible}
                      </Link>
                    </Tooltip>
                  )
                }
              />
            )}
            {provider.sentAt && (
              <Field label={DRAWER.linkSent} value={provider.sentAt} />
            )}
            {provider.paidAt && (
              <Field label={DRAWER.paid} value={provider.paidAt} />
            )}
            {provider.declinedAt && (
              <Field label={DRAWER.declined} value={provider.declinedAt} />
            )}
          </Stack>

          <Divider />

          {/* Read by default, edited on request. Everything a CAM can change lives
              in the one form, so there is one Save and one Cancel rather than a
              field that commits on its own. An unwritten note says so, for the
              same reason the answers above do. */}
          {draft ? (
            <CamAnswerForm
              draft={draft}
              onChange={(patch) =>
                setDraft((prev) => (prev ? { ...prev, ...patch } : prev))
              }
              canEditAnswers={canEditAnswers}
            />
          ) : (
            <Field label={DRAWER.note} value={provider.note} />
          )}
        </Stack>
      )}
    </Drawer>
  );
};

RowDrawer.displayName = 'RowDrawer';
export default RowDrawer;
